const { ethers } = require('ethers');
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
const keyOf = value => JSON.stringify(value, (_key, item) => typeof item === 'bigint' ? item.toString() : item);

// One scheduler for all reads. No private data or full endpoint URLs enter metrics.
class RpcPool {
  constructor(providers, {hedgeMs = 350, cooldownMs = 15000} = {}) {
    this.entries = providers.map(provider => ({provider, latency:350, active:0, failures:0, until:0, used:0, checked:0, head:0, headAt:0, methods:new Map(), queue:[]}));
    this.hedgeMs=hedgeMs; this.cooldownMs=cooldownMs; this.inflight=new Map(); this.cache=new Map(); this.sequence=0; this.closed=false;
  }
  ranked(method) {
    const now=Date.now();
    const sorted=[...this.entries].sort((a,b) => {
      const score=e => (Math.max(e.until,e.methods.get(method)||0)>now ? 100000 : 0)+e.active*500+e.latency+e.failures*500;
      return score(a)-score(b) || a.used-b.used;
    });
    const available=sorted.filter(e=>Math.max(e.until,e.methods.get(method)||0)<=now);
    return available.length ? available : sorted;
  }
  async invoke(entry, method, work, priority=0) {
    if(this.closed) throw new Error('RPC manager closed');
    if(entry.active>=(priority>0?6:4)) await new Promise((resolve,reject)=>{
      entry.queue.push({resolve,reject,priority});entry.queue.sort((a,b)=>b.priority-a.priority);
    }); else entry.active++;
    const started=Date.now();entry.used=++this.sequence;
    try {
      if(Date.now()-entry.checked>60000) {
        if(!entry.networkCheck) entry.networkCheck=entry.provider.getNetwork().then(network=>{
          if(network.chainId!==10n)throw new Error('Red incorrecta: se requiere Optimism.');entry.checked=Date.now();
        }).finally(()=>{entry.networkCheck=null;});
        await entry.networkCheck;
      }
      const value=await work(entry.provider,entry);
      entry.latency=entry.latency*0.7+(Date.now()-started)*0.3;entry.failures=0;entry.until=0;
      return value;
    } catch(error) {
      const message=[error?.shortMessage,error?.message,error?.error?.message,error?.info?.error?.message].filter(Boolean).join(' ');
      if(/archive|historical|method.*(support|available)|personal token/i.test(message))entry.methods.set(method,Date.now()+300000);
      else if(!['CALL_EXCEPTION','INSUFFICIENT_FUNDS','NONCE_EXPIRED','ACTION_REJECTED'].includes(error.code)) {
        entry.failures++;entry.until=Date.now()+Math.min(60000,this.cooldownMs*entry.failures);
      }
      throw error;
    } finally {
      entry.active--;
      while(entry.queue.length){const index=entry.queue.findIndex(q=>entry.active<(q.priority>0?6:4));if(index<0)break;const [next]=entry.queue.splice(index,1);entry.active++;next.resolve();}
    }
  }
  request(method, work, {key, ttl=0, nonNull=false, priority=0}={}) {
    if(this.closed)return Promise.reject(new Error('RPC manager closed'));
    const cached=key && this.cache.get(key);
    if(cached && Date.now()<cached.until)return Promise.resolve(cached.value);
    if(key && this.inflight.has(key))return this.inflight.get(key);
    const entries=this.ranked(method);
    const promise=new Promise((resolve,reject)=>{
      let index=0,active=0,done=false,sawNull=false,lastError,timer;
      const finish=(error,value)=>{if(done)return;done=true;clearTimeout(timer);error?reject(error):resolve(value);};
      const launch=()=>{
        if(done)return;
        if(index>=entries.length){if(!active)finish(sawNull?null:lastError||new Error('No RPC available'),null);return;}
        const entry=entries[index++];active++;
        this.invoke(entry,method,work,priority).then(value=>{
          if(nonNull && value==null)sawNull=true;else finish(null,value);
        },error=>{lastError=error;}).finally(()=>{
          active--;if(!done)launch();
        });
      };
      launch();timer=setTimeout(()=>{if(!done && active<2)launch();},this.hedgeMs);
    });
    if(key){this.inflight.set(key,promise);promise.then(value=>{if(ttl && value!=null)this.cache.set(key,{value,until:Date.now()+ttl});},()=>{}).finally(()=>this.inflight.delete(key));}
    return promise;
  }
  destroy() {this.closed=true;this.cache.clear();for(const entry of this.entries){for(const queued of entry.queue.splice(0))queued.reject(new Error('RPC manager closed'));entry.provider.destroy();}}
}

class AdaptiveProvider extends ethers.AbstractProvider {
  constructor(urls, options={}) {
    super(10,{cacheTimeout:250,pollingInterval:1500});
    const providers=urls.map(url=>{const request=new ethers.FetchRequest(url);request.timeout=6000;return new ethers.JsonRpcProvider(request,undefined,{batchMaxCount:1,cacheTimeout:250});});
    this.providerConfigs=providers.map(provider=>({provider}));this.pool=new RpcPool(providers,options);
  }
  async _detectNetwork(){return this.pool.request('network',async()=>ethers.Network.from(10),{key:'network',ttl:60000});}
  async _perform(params) {
    if(params.method==='broadcastTransaction')throw new Error('Read provider cannot broadcast');
    const key=keyOf(params);
    const work=async(p,entry)=>{
      // Exclude endpoints lagging behind the recent head observed by this manager.
      if(params.method==='call' || params.method==='estimateGas' || params.method==='getTransactionCount' || params.method==='getBalance') {
        if(Date.now()-entry.headAt>3000){entry.head=await p.getBlockNumber();entry.headAt=Date.now();}
        const best=Math.max(...this.pool.entries.filter(e=>Date.now()-e.headAt<10000).map(e=>e.head),0);
        if(entry.head<best-2)throw new Error('RPC block is stale');
      }
      const result=await p._perform(params);
      if(params.method==='getBlockNumber'){
        const best=Math.max(...this.pool.entries.filter(e=>Date.now()-e.headAt<10000).map(e=>e.head),0);
        entry.head=Number(result);entry.headAt=Date.now();
        if(entry.head<best-2)throw new Error('RPC block is stale');
      }
      return result;
    };
    if(params.method==='getTransactionCount' && params.blockTag==='pending') {
      if(this.pool.inflight.has(key))return this.pool.inflight.get(key);
      const task=Promise.allSettled(this.pool.ranked(params.method).slice(0,2).map(entry=>this.pool.invoke(entry,params.method,work,10))).then(results=>{
        const values=results.filter(r=>r.status==='fulfilled').map(r=>BigInt(r.value));
        if(!values.length)return this.pool.request(params.method,work,{priority:10});
        return ethers.toQuantity(values.reduce((a,b)=>a>b?a:b));
      });
      this.pool.inflight.set(key,task);task.then(()=>this.pool.inflight.delete(key),()=>this.pool.inflight.delete(key));return task;
    }
    return this.pool.request(params.method,work,{key,ttl:params.method==='getBlockNumber'?750:0,nonNull:['getTransactionReceipt','getTransaction'].includes(params.method),priority:['estimateGas','getTransactionReceipt','getTransactionCount'].includes(params.method)?10:0});
  }
  async getFeeData(){return this.pool.request('fees',p=>p.getFeeData(),{priority:10});}
  destroy(){this.pool.destroy();super.destroy();}
}

// Receipt errors are transport errors until a receipt proves otherwise. Never resend here.
async function waitForReceipt(provider, hash, {timeoutMs=120000, pollMs=1500, confirms=1}={}) {
  const deadline=Date.now()+timeoutMs;let cause;
  do {
    try {
      const receipt=await provider.getTransactionReceipt(hash);
      if(receipt && (confirms<=1 || await receipt.confirmations()>=confirms)) {
        if(receipt.status===0)throw Object.assign(new Error('Transaction reverted onchain.'),{code:'CALL_EXCEPTION',receipt});
        if(receipt.status===1)return receipt;
      }
    } catch(error){if(error.receipt)throw error;cause=error;}
    if(Date.now()>=deadline)break;
    await delay(Math.min(pollMs,deadline-Date.now()));
  }while(Date.now()<deadline);
  throw Object.assign(new Error('Transaction sent; confirmation pending. Do not repeat the swap. Check Activity or the explorer.'),{code:'TRANSACTION_PENDING',transactionHash:hash,cause});
}
module.exports={RpcPool,AdaptiveProvider,waitForReceipt};
